// src/pages/DownloadsPage.js
import React, { useState, useEffect } from 'react';
import downloadService from '../services/downloadService';
import DownloadModal from '../components/DownloadModal';
import SEO from '../components/SEO';
import '../styles/DownloadsPage.css';

const DownloadsPage = () => {
  const [downloads, setDownloads] = useState([]);
  const [filteredDownloads, setFilteredDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDownload, setSelectedDownload] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchDownloads();
  }, []);

  useEffect(() => {
    filterDownloads();
  }, [downloads, activeCategory, searchTerm]);

  const fetchDownloads = async () => {
    try {
      setLoading(true);
      const data = await downloadService.getAllDownloads();
      setDownloads(data || []);
      setFilteredDownloads(data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching downloads:', err);
      setError('Failed to load downloads. Please try again later.');
      setLoading(false);
    }
  };

  const filterDownloads = () => {
    let result = downloads;

    // Filter by category
    if (activeCategory !== 'All') {
      result = result.filter(download => download.category === activeCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(download =>
        download.title.toLowerCase().includes(term) ||
        download.description.toLowerCase().includes(term) ||
        (download.tags && download.tags.some(tag => tag.toLowerCase().includes(term)))
      );
    }

    setFilteredDownloads(result);
  };

  const categories = ['All', ...new Set(downloads.map(d => d.category))];

  const handleDownloadClick = (download) => {
    setSelectedDownload(download);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedDownload(null);
  };

  const getFileTypeIcon = (fileType) => {
    if (!fileType) return 'fa-file';
    
    if (fileType.includes('pdf')) return 'fa-file-pdf';
    if (fileType.includes('word') || fileType.includes('document')) return 'fa-file-word';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'fa-file-excel';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return 'fa-file-powerpoint';
    if (fileType.includes('image')) return 'fa-file-image';
    if (fileType.includes('video')) return 'fa-file-video';
    if (fileType.includes('zip') || fileType.includes('compressed')) return 'fa-file-archive';
    
    return 'fa-file';
  };

  if (loading) {
    return (
      <div className="downloads-page">
        <div className="downloads-header">
          <div className="container">
            <h1 className="downloads-title">Free Resources</h1>
            <p className="downloads-description">
              Download our free guides, templates, and tools to help you build and grow your business
            </p>
          </div>
        </div>
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading resources...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="downloads-page">
        <div className="downloads-header">
          <div className="container">
            <h1 className="downloads-title">Free Resources</h1>
            <p className="downloads-description">
              Download our free guides, templates, and tools to help you build and grow your business
            </p>
          </div>
        </div>
        <div className="container">
          <div className="error-container">
            <h2>Something went wrong</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="downloads-page">
      <SEO 
        title="Downloads"
        description="Download free business formation templates, guides, and resources. Essential documents for entrepreneurs and small business owners."
        keywords="business templates, free downloads, business forms, entrepreneurship resources, business documents, startup resources"
        url="https://legate-llp.com/downloads"
      />
      <div className="downloads-header">
        <div className="container">
          <h1 className="downloads-title">Free Resources</h1>
          <p className="downloads-description">
            Download our free guides, templates, and tools to help you build and grow your business
          </p>
        </div>
      </div>

      <div className="container">
        <div className="downloads-filters">
          <div className="category-filter">
            <h3>Categories</h3>
            <ul className="category-list">
              {categories.map(category => (
                <li key={category}>
                  <button
                    className={`category-button ${activeCategory === category ? 'active' : ''}`}
                    onClick={() => setActiveCategory(category)}
                  >
                    {category}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="search-filter">
            <input
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="downloads-content">
          {filteredDownloads.length > 0 ? (
            <div className="downloads-grid">
              {filteredDownloads.map(download => (
                <div className="download-card" key={download.id}>
                  {download.thumbnail && (
                    <div className="download-thumbnail">
                      <img src={download.thumbnail} alt={download.title} />
                    </div>
                  )}
                  <div className="download-content">
                    <div className="download-header">
                      <i className={`fas ${getFileTypeIcon(download.fileType)} download-icon`}></i>
                      <span className="download-category">{download.category}</span>
                    </div>
                    <h3 className="download-title">{download.title}</h3>
                    <p className="download-description">{download.description}</p>
                    <div className="download-meta">
                      <span className="download-size">
                        <i className="fas fa-file-download"></i>
                        {downloadService.formatFileSize(download.fileSize)}
                      </span>
                      {download.downloadCount > 0 && (
                        <span className="download-count">
                          <i className="fas fa-download"></i>
                          {download.downloadCount} downloads
                        </span>
                      )}
                    </div>
                    <button
                      className="download-button"
                      onClick={() => handleDownloadClick(download)}
                    >
                      {download.buttonText || 'Download Now'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-results">
              <h3>No resources found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      </div>

      {/* Download Modal */}
      {selectedDownload && (
        <DownloadModal
          show={showModal}
          onHide={handleModalClose}
          download={selectedDownload}
        />
      )}
    </div>
  );
};

export default DownloadsPage;
